import React from "react";

import { Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";

import {
  MinusSVG,
  QuestionSVG,
  TickOutlineSVG,
  Tier1BadgeSVG,
  Tier2BadgeSVG,
  Tier3BadgeSVG,
  Tier4BadgeSVG,
  Tier4BadgeUkSVG,
} from "assets/icons";
import {
  Tier1ShortBadgeSVG,
  Tier2ShortBadgeSVG,
  Tier3ShortBadgeSVG,
  Tier4ShortBadgeSVG,
} from "components/TierBadge/icons";

import styles from "./ComparisonTiersTable.module.scss";

type RowData = {
  isTitle?: boolean;
  isSpacer?: boolean;
  key: string;
  features?: React.ReactNode;
  tier_1?: boolean | "soon";
  tier_2?: boolean | "soon";
  tier_3?: boolean | "soon";
  tier_4?: boolean | "soon";
};

export const getTableData = (_isUkFinProm?: boolean): RowData[] => [
  { key: "spacer-virtual-assets", isSpacer: true },
  { key: "section-virtual-assets", isTitle: true, features: "VIRTUAL ASSETS" },

  {
    key: "va-deposit-withdraw",
    features: "Deposit & Withdraw Virtual Assets",
    tier_1: true,
    tier_2: true,
    tier_3: true,
    tier_4: true,
  },
  {
    key: "va-trade",
    features: "Trade Virtual Assets (BTC, ETH, USDT)",
    tier_1: true,
    tier_2: true,
    tier_3: true,
    tier_4: true,
  },
  {
    key: "va-stake-eth",
    features: "Stake Ethereum (ETH) for weekly income",
    tier_1: true,
    tier_2: true,
    tier_3: true,
    tier_4: true,
  },
  {
    key: "va-auto-restaking",
    features: (
      <span className={styles.featureWithTooltip}>
        <span>Auto Restaking for weekly compounded income</span>
        <Tooltip title='Staking rewards are automatically reinvested every week to maximize your compound interest'>
          <span className={styles.tooltipIcon} role='button' tabIndex={0}>
            <QuestionSVG />
          </span>
        </Tooltip>
      </span>
    ),
    tier_1: true,
    tier_2: true,
    tier_3: true,
    tier_4: true,
  },

  { key: "spacer-public-shares", isSpacer: true },
  { key: "section-public-shares", isTitle: true, features: "PUBLIC SHARES" },

  {
    key: "ps-sell-post-ipo",
    features: "Sell Public Shares (Post-IPO)",
    tier_1: true,
    tier_2: true,
    tier_3: true,
    tier_4: true,
  },

  { key: "spacer-private-equity", isSpacer: true },
  { key: "section-private-equity", isTitle: true, features: "PRIVATE EQUITY" },

  {
    key: "pe-secondary-market",
    features: (
      <span className={styles.featureWithTooltip}>
        <span>Buy & Sell on Secondary Market</span>
        <Tooltip title='Unlock liquidity. Exit your private equity positions early through our exclusive peer-to-peer marketplace. Note that buying on the secondary market is currently available to non-US investors only'>
          <span className={styles.tooltipIcon} role='button' tabIndex={0}>
            <QuestionSVG />
          </span>
        </Tooltip>
      </span>
    ),
    tier_1: false,
    tier_2: false,
    tier_3: false,
    tier_4: true,
  },
  {
    key: "pe-primary-market-deals",
    features: "Access Primary Market Deals",
    tier_1: false,
    tier_2: false,
    tier_3: false,
    tier_4: true,
  },
];

const renderTierCell = (value?: boolean | "soon") => {
  if (value === "soon") return <span className={styles.soonBadge}>SOON</span>;
  return value ? <TickOutlineSVG /> : <MinusSVG />;
};

const fullRow = { colSpan: 5 };

export const getColumns = (isUkFinProm?: boolean) =>
  [
    {
      title: "Features",
      dataIndex: "features",
      key: "features",
      width: "360px",
      render: (_, record) => {
        if (record.isSpacer) return null;
        if (record.isTitle) return <div className={styles.sectionTitle}>{record.features}</div>;
        return record.features;
      },
      onCell: (record) => (record.isSpacer || record.isTitle ? fullRow : {}),
    },
    {
      title: <Tier1BadgeSVG width='140px' height='34px' />,
      key: "tier_1",
      render: (_, record) => renderTierCell(record.tier_1),
      onCell: (record) => (record.isSpacer || record.isTitle ? { colSpan: 0 } : {}),
    },
    {
      title: <Tier2BadgeSVG width='140px' height='34px' />,
      key: "tier_2",
      render: (_, record) => renderTierCell(record.tier_2),
      onCell: (record) => (record.isSpacer || record.isTitle ? { colSpan: 0 } : {}),
    },
    {
      title: <Tier3BadgeSVG width='140px' height='34px' />,
      key: "tier_3",
      render: (_, record) => renderTierCell(record.tier_3),
      onCell: (record) => (record.isSpacer || record.isTitle ? { colSpan: 0 } : {}),
    },
    {
      title: <Tier4BadgeSVG width='140px' height='34px' />,
      key: "tier_4",
      render: (_, record) => renderTierCell(record.tier_4),
      onCell: (record) => (record.isSpacer || record.isTitle ? { colSpan: 0 } : {}),
    },
  ] as ColumnsType<RowData>;

const MOBILE_COLSPAN = 4;

export const getMobileColumns = (isUkFinProm?: boolean) =>
  [
    {
      title: <Tier1ShortBadgeSVG />,
      key: "tier_1",
      render: (_, record) => {
        if (record.isSpacer) return null;
        if (record.isTitle) return <div className={styles.sectionTitle}>{record.features}</div>;
        return renderTierCell(record.tier_1);
      },
      onCell: (record) => {
        if (record.isSpacer) return { colSpan: MOBILE_COLSPAN, className: styles.spacerCell };
        if (record.isTitle) return { colSpan: MOBILE_COLSPAN };
        return {};
      },
    },
    {
      title: <Tier2ShortBadgeSVG />,
      key: "tier_2",
      render: (_, record) => {
        if (record.isSpacer || record.isTitle) return null;
        return renderTierCell(record.tier_2);
      },
      onCell: (record) => (record.isSpacer || record.isTitle ? { colSpan: 0 } : {}),
    },
    {
      title: <Tier3ShortBadgeSVG />,
      key: "tier_3",
      render: (_, record) => {
        if (record.isSpacer || record.isTitle) return null;
        return renderTierCell(record.tier_3);
      },
      onCell: (record) => (record.isSpacer || record.isTitle ? { colSpan: 0 } : {}),
    },
    {
      title: <Tier4ShortBadgeSVG />,
      key: "tier_4",
      render: (_, record) => {
        if (record.isSpacer || record.isTitle) return null;
        return renderTierCell(record.tier_4);
      },
      onCell: (record) => (record.isSpacer || record.isTitle ? { colSpan: 0 } : {}),
    },
  ] as ColumnsType<RowData>;
